import React, { Suspense, useEffect } from 'react'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import _ from 'lodash'
import { lazyImport } from 'utils/basic'
import { getToken } from 'utils/authLocalStorage'
import LoadingView from 'components/LoadingView'

const WWBrowse = lazyImport(() => import('views/WWBrowse'))
const WWWorkoutDetail = lazyImport(() => import('views/WWWorkoutDetail'))
const WWBrowseAll = lazyImport(() => import('views/WWBrowseAll'))
const WWFeedback = lazyImport(() => import('views/WWFeedback'))
const WWActivity = lazyImport(() => import('views/WWActivity'))
const MobileTrainerVideo = lazyImport(() => import('views/MobileTrainerVideo'))
const MobileInprogress = lazyImport(() => import('views/MobileInprogress'))
const Rating = lazyImport(() => import('views/Rating'))
const ErrorPage = lazyImport(() => import('views/ErrorPage'))
const Challenges = lazyImport(() => import('views/Challenges'))
const ChallengesAll = lazyImport(() => import('views/ChallengesAll'))
const ChallengeDetail = lazyImport(() => import('views/ChallengeDetail'))
const BrowseSearch = lazyImport(() => import('views/BrowseSearch'))
const Vimeo = lazyImport(() => import('views/Vimeo'))
const MuscleDefenseCategoryDetail = lazyImport(() => import('views/MuscleDefenseCategoryDetail'))
const YourWorkoutPlan = lazyImport(() => import('views/YourWorkoutPlan'))
const VideoPlayer = lazyImport(() => import('Revive/views/VideoPlayer'))

const Browse = lazyImport(() => import('Revive/views/Browse'))
const ReviveBrowseSearch = lazyImport(() => import('Revive/views/BrowseSearch'))
const ReviveOnboarding = lazyImport(() => import('Revive/views/Onboarding'))
const ReviveWeightTracker = lazyImport(() =>
  import('Revive/views/ReviveWeightTracker')
)
const ReviveBrowseAll = lazyImport(() => import('Revive/views/BrowseAll'))
const History = lazyImport(() => import('Revive/views/History'))
const ReviveInprogress = lazyImport(() => import('Revive/views/Inprogress'))
const ReviveRating = lazyImport(() => import('Revive/views/Rating'))
const WorkoutDetail = lazyImport(() => import('Revive/views/WorkoutDetail'))
const ReviveErrorPage = lazyImport(() => import('Revive/views/ErrorPage'))
const ProgramDetail = lazyImport(() => import('Revive/views/ProgramDetail'))
const ProgramList = lazyImport(() => import('Revive/views/ProgramList'))
const RecipeDetail = lazyImport(() => import('Revive/views/RecipeDetail'))
const RecipeList = lazyImport(() => import('Revive/views/RecipeList'))
const ReviveYourWorkoutPlan = lazyImport(() =>
  import('Revive/views/YourWorkoutPlan')
)

const ScrollToTop = ({ isRevive }) => {
  const { pathname } = useLocation()

  useEffect(() => {
    if (isRevive && pathname !== '/partner/revive/browse') {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      })

      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
  }, [isRevive, pathname])

  return null
}

const PrivateRoute = ({
  component: Component,
  fallback,
  mobileEnable,
  autoLoginLoading,
  darkMode,
  ...rest
}) => {
  const token = getToken()
  return (
    <Route
      {...rest}
      render={(props) =>
        token ? (
          <Component
            {...props}
            darkMode={darkMode}
            autoLoginLoading={autoLoginLoading}
          />
        ) : (
          <Redirect
            to={{
              pathname: fallback || '/partner/browse',
              state: _.get(props, 'location.state', {}),
            }}
          />
        )
      }
    />
  )
}
const PartnerRoutes = ({ autoLoginLoading, darkMode }) => {
  return (
    <Switch>
      <Route
        exact
        path='/partner/browse/plan/:planId'
        render={(props) => (
          <YourWorkoutPlan
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <PrivateRoute
        exact
        path='/mobile/inprogress/:workoutId'
        component={MobileInprogress}
        darkMode={darkMode}
        autoLoginLoading={autoLoginLoading}
      />
      <Route
        exact
        path='/partner/browse/challenge'
        render={(props) => (
          <Challenges
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/browse/challenge-all'
        render={(props) => (
          <ChallengesAll
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/browse/program-all'
        render={(props) => (
          <ChallengesAll
            {...props}
            isProgram={true}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/browse/search'
        render={(props) => (
          <BrowseSearch
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/muscledefense/detail'
        render={(props) => (
          <MuscleDefenseCategoryDetail
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/browse/challenge/:challengeId'
        render={(props) => (
          <ChallengeDetail
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <PrivateRoute
        exact
        path='/mobile/video'
        component={MobileTrainerVideo}
        darkMode={darkMode}
        autoLoginLoading={autoLoginLoading}
      />
      <Route
        exact
        path='/partner/browse'
        render={(props) => (
          <WWBrowse
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/vimeo'
        render={(props) => (
          <Vimeo
            {...props}
            darkMode={darkMode}
            autoLoginLoading={autoLoginLoading}
          />
        )}
      />
      <Route
        exact
        path='/partner/browse/workout/:workoutId'
        render={(props) => (
          <WWWorkoutDetail
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <Route
        exact
        path='/partner/browse/:field/:typeValue?'
        render={(props) => (
          <WWBrowseAll
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <PrivateRoute
        exact
        path='/partner/feedback'
        component={WWFeedback}
        darkMode={darkMode}
        autoLoginLoading={autoLoginLoading}
      />
      <Route
        exact
        path='/partner/activity'
        render={(props) => (
          <WWActivity
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
      <PrivateRoute
        exact
        path='/partner/rating/:workoutId'
        component={Rating}
        darkMode={darkMode}
        autoLoginLoading={autoLoginLoading}
      />
      <Route
        exact
        path='/partner/error'
        render={(props) => (
          <ErrorPage
            {...props}
            darkMode={darkMode}
            autoLoginLoading={autoLoginLoading}
          />
        )}
        darkMode={darkMode}
      />
      <Route
        render={(props) => (
          <WWBrowse
            {...props}
            autoLoginLoading={autoLoginLoading}
            darkMode={darkMode}
          />
        )}
      />
    </Switch>
  )
}

const ReviveRoutes = ({ autoLoginLoading, darkMode }) => {
  return (
    <Switch>
      <Route
        exact
        path='/partner/revive/browse/plan'
        render={(props) => (
          <ReviveYourWorkoutPlan
            {...props}
            autoLoginLoading={autoLoginLoading}
          />
        )}
      />
      <Route
        exact
        path='/partner/revive/browse/search'
        render={(props) => {
          return (
            <ReviveBrowseSearch
              {...props}
              autoLoginLoading={autoLoginLoading}
            />
          )
        }}
      />
      <Route
        exact
        path='/partner/revive/browse'
        render={(props) => {
          return <Browse {...props} autoLoginLoading={autoLoginLoading} />
        }}
      />

      <Route
        exact
        path='/partner/revive/onboarding'
        render={(props) => {
          return (
            <ReviveOnboarding {...props} autoLoginLoading={autoLoginLoading} />
          )
        }}
      />
      <Route
        exact
        path='/partner/revive/weight-tracker'
        render={(props) => {
          return (
            <ReviveWeightTracker
              {...props}
              autoLoginLoading={autoLoginLoading}
            />
          )
        }}
      />
      <Route
        exact
        path='/partner/revive/browse/workout/:workoutId'
        render={(props) => (
          <WorkoutDetail {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/activity'
        render={(props) => (
          <History {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/mobile/inprogress/:workoutId'
        render={(props) => (
          <ReviveInprogress {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/browse/program-all'
        render={(props) => (
          <ProgramList {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/browse/recipe-all'
        render={(props) => (
          <RecipeList {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/program/detail/:id/:week?'
        render={(props) => (
          <ProgramDetail {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/rating/:workoutId'
        render={(props) => (
          <ReviveRating {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />

      <Route
        path='/partner/revive/browse/:field/:typeValue?'
        render={(props) => (
          <ReviveBrowseAll {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/meal/:mealId'
        render={(props) => (
          <RecipeDetail {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/meal/pro_video/:nutritionId'
        render={(props) => (
          <VideoPlayer {...props} autoLoginLoading={autoLoginLoading} />
        )}
      />
      <Route
        exact
        path='/partner/revive/error'
        render={(props) => <ReviveErrorPage {...props} />}
      />
      <Route
        render={(props) => {
          return (
            <Browse
              {...props}
              autoLoginLoading={autoLoginLoading}
              darkMode={darkMode}
            />
          )
        }}
      />
    </Switch>
  )
}

const Routes = ({ autoLoginLoading, darkMode, isRevive }) => {
  return (
    <Suspense fallback={<LoadingView />}>
      <ScrollToTop isRevive={isRevive} />
      {isRevive ? (
        <ReviveRoutes {...{ autoLoginLoading, darkMode }} />
      ) : (
        <PartnerRoutes {...{ autoLoginLoading, darkMode }} />
      )}
    </Suspense>
  )
}

export default Routes
